export interface IPhoneStatusBarProps {
  theme?: 'light' | 'dark';
}

// Static iOS status bar for the onboarding mockup: fixed "9:41" clock plus
// signal/wifi/battery glyphs, matching the real iOS status bar layout.
// Rendered as part of the mockup screen area (react-device-mockup's
// `hideStatusBar` prop) so it can be themed per-scene.
//
// Sizing/position calibrated by pixel-diffing two same-size screenshots of
// IPhoneMockup: this component's rendered output vs. the chrome-baked home
// screen scene (a plain scaled export of the Figma frame, so its status bar
// is always correctly proportioned). The two aren't symmetric left/right —
// the clock's glyph has more left-bearing than the icons have trailing
// space — so the insets below are asymmetric on purpose, matching what was
// measured rather than a assumed-symmetric guess.
export function IPhoneStatusBar({ theme = 'light' }: IPhoneStatusBarProps) {
  const foregroundClass = theme === 'dark' ? 'text-white' : 'text-black';

  return (
    <div
      className={`flex w-full items-center justify-between pb-1 pl-[45px] pr-[22px] pt-[13px] text-[12px] font-semibold ${foregroundClass}`}
    >
      <span>9:41</span>
      {/* This row already carries the calibrated pr-[22px]/pt-[13px] inset
          for the whole status bar — StatusBarIcons' own copy of that same
          inset (for its *other* caller, LockScreenScene, which has no such
          wrapper) would otherwise stack on top of this one, landing the
          icon cluster visibly down-and-left of its calibrated spot. */}
      <StatusBarIcons className={foregroundClass} withInset={false} />
    </div>
  );
}

// Just the signal/wifi/battery cluster, same calibrated sizing as
// IPhoneStatusBar above — for scenes that draw their own time display (e.g.
// LockScreenScene's big clock) and shouldn't also show the small corner
// clock real iOS hides in that case.
export function StatusBarIcons({
  className = '',
  // Defaults on for LockScreenScene's bare, unpadded usage — IPhoneStatusBar
  // opts out since it supplies this same inset itself (see its own comment).
  withInset = true,
}: {
  className?: string;
  withInset?: boolean;
}) {
  return (
    <div
      className={`flex w-full items-center justify-end gap-[5px] ${withInset ? 'pr-[22px] pt-[13px]' : ''} ${className}`}
    >
      <SignalIcon className="h-[10px] w-[16px]" />
      <WifiIcon className="h-[10px] w-[14px]" />
      <BatteryIcon className="h-[10px] w-[23px]" />
    </div>
  );
}

function SignalIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 17 11"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <rect x="0" y="7" width="3" height="4" rx="0.6" />
      <rect x="4.5" y="5" width="3" height="6" rx="0.6" />
      <rect x="9" y="3" width="3" height="8" rx="0.6" />
      <rect x="13.5" y="0" width="3" height="11" rx="0.6" />
    </svg>
  );
}

function WifiIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 15 11"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M7.5 10.2a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2Z"
        fill="currentColor"
      />
      <path
        d="M4.3 6.6a4.6 4.6 0 0 1 6.4 0"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M1.5 3.7a8.6 8.6 0 0 1 12 0"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BatteryIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 11"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect
        x="0.75"
        y="0.75"
        width="19.5"
        height="9.5"
        rx="2.5"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="1"
      />
      <rect x="2" y="2" width="17" height="7" rx="1.5" fill="currentColor" />
      <rect
        x="21"
        y="3.5"
        width="1.6"
        height="4"
        rx="0.8"
        fill="currentColor"
        fillOpacity="0.4"
      />
    </svg>
  );
}
